import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEditorState, type Editor } from '@tiptap/react'
import {
  NodeResizeControl,
  NodeToolbar,
  Position,
  useStore,
  useStoreApi,
  ViewportPortal,
  type Node,
  type NodeProps,
} from '@xyflow/react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { CanvasNote, UpdateCanvasNote } from '@/src/renderer/lib/db/models'
import type { ResizeCorner } from '../utils/canvasAlignment'
import { canvasTextSizeOf, getCanvasTextInk, MIN_CANVAS_TEXT_SIZE, type CanvasTextHighlightKey } from '../utils/canvasText'
import { CANVAS_TEXT_DEFAULT_KEY, isCanvasTextEmpty } from '../utils/canvasTextContent'
import {
  applyCanvasTextFormat,
  canvasTextFormatFrom,
  canvasTextFormatOf,
  canvasTextSelectionOf,
  clearCanvasTextStyle,
  formatCanvasText,
  previewCanvasTextFormat,
  type CanvasTextFormatChange,
} from '../utils/canvasTextFormat'
import type { CanvasTextStyleAttr } from '../utils/canvasTextSchema'
import { getCanvasNoteFont } from '../utils/canvasFonts'
import { randomCanvasTextMarkerLook } from '../utils/canvasTextMarker'
import { canvasNoteRotations, rotatedBounds } from '../utils/canvasRotation'
import { useMeasuredTextSize } from '../hooks/useMeasuredTextSize'
import {
  useCanvasPointerBusy,
  useOptionsBarPlacement,
  useSoloSelected,
} from '../hooks/useCanvasOptionsBar'
import { usePresence } from '../hooks/usePresence'
import { useOptionPreview } from '../hooks/useOptionPreview'
import type { CanvasColorPreview, CanvasFontPreview, CanvasOptionPreview } from '../optionsBar/CanvasOptionsBar.types'
import { useRotateDrag } from '../hooks/useRotateDrag'
import { RotateHandles } from '../RotateHandles'
import { CanvasOptionsBar } from '../optionsBar'
import { OPTIONS_BAR_OFFSET } from '../optionsBar/CanvasOptionsBar.types'
import { CanvasTextEditor } from './CanvasTextEditor'
import { CanvasTextContent } from './CanvasTextContent'
import type { TextNodeData } from './TextNode.types'
import './TextNode.scss'

/** Die Knöpfe der Leiste — Formatieren gehört zum Text (ADR 0029). */
const BAR_BUTTONS = ['color', 'font', 'size', 'marks', 'highlight', 'list', 'align', 'delete'] as const

/** Welches Feld der Zeile den Grundwert eines Stils trägt. */
const BASE_FIELD = { color: 'color', font: 'font', size: 'fontSize' } as const satisfies Record<CanvasTextStyleAttr, keyof CanvasNote>

/** Die vier Ecken, an denen Text und Box zusammen wachsen — Kanten gibt es nicht (CONTEXT.md, Text). */
const RESIZE_CORNERS: readonly ResizeCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right']

/**
 * Wie lange die gezogene Größe nach dem Loslassen stehen bleibt, falls die neue
 * Schriftgröße nicht zurückkommt — ein Netz, nicht der Weg (siehe `resize`).
 */
const SETTLE_FALLBACK_MS = 600

/**
 * Der **Text** — freier Text auf der Pinnwand, ohne Papier (CONTEXT.md, Text;
 * ADR 0029). Seine Box ist immer so groß wie sein Inhalt.
 *
 * **Was er mit Notiz und Memo teilt:** Auswählen, Verschieben, Einrasten,
 * Duplizieren, Löschen, **Neigung** und die **Options-Leiste** — dieselben
 * Rückrufe, dieselben Wege über `CanvasFlow`. **Anlegepunkte trägt er nicht**
 * (entschieden 2026-09-23): Ein Plus an jeder Kante passt nicht zu einer
 * Zeile Text.
 *
 * **Was ihn unterscheidet:** Die vier Ecken skalieren die SCHRIFT, die Box folgt
 * ihr. Leer gibt es ihn in Ruhe nicht — wer ihn leer verlässt, lässt ihn
 * verschwinden (`onEmptied`).
 *
 * @example
 * <ReactFlow nodeTypes={{ text: TextNode }} … />
 */
export const TextNode: React.FC<NodeProps<Node<TextNodeData>>> = ({
  data,
  selected,
  width: nodeWidth,
  height: nodeHeight,
  positionAbsoluteX,
  positionAbsoluteY,
}) => {
  const { t } = useTranslation()
  const dataSource = useDataSource()
  const store = useStoreApi()
  const { note, startEditing = false, editingEnabled = true, onResizeStart, onEdit, onDelete, editRequest, onEditingChange, onEmptied, colorPreview, fontPreview } = data

  // Ohne Canvas (Story, Test) schreibt der Text selbst — ohne Schritt, wie Notiz und Memo.
  const edit = useCallback((id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) => {
    if (onEdit) onEdit(id, before, after)
    else void dataSource.updateCanvasNote(id, after)
  }, [onEdit, dataSource])

  const [draft, setDraft] = useState(note.content)
  const [draftEmpty, setDraftEmpty] = useState(() => isCanvasTextEmpty(note.content))
  const [isEditing, setIsEditing] = useState(startEditing)
  const isEditingRef = useRef(isEditing)
  isEditingRef.current = isEditing

  // Vom Datensatz spiegeln — nie mitten im Tippen (dieselbe Falle wie beim Memo).
  useEffect(() => {
    if (isEditingRef.current) return
    setDraft(note.content)
    setDraftEmpty(isCanvasTextEmpty(note.content))
  }, [note.content])

  const onEditingChangeRef = useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange
  useEffect(() => {
    onEditingChangeRef.current?.(note.id, isEditing)
    return () => { if (isEditing) onEditingChangeRef.current?.(note.id, false) }
  }, [isEditing, note.id])

  // Die Bitte von außen (Doppelklick auf den Tauschpunkt) — wie beim Memo.
  const editRequestRef = useRef(editRequest)
  useEffect(() => {
    if (editRequest === editRequestRef.current) return
    editRequestRef.current = editRequest
    if (!editRequest || !editingEnabled) return
    store.getState().addSelectedNodes([note.id])
    setIsEditing(true)
  }, [editRequest, editingEnabled, store, note.id])

  const openEditor = useCallback(() => {
    if (!editingEnabled) return
    store.getState().addSelectedNodes([note.id])
    setIsEditing(true)
  }, [editingEnabled, store, note.id])

  /*
    Enter öffnet den ausgewählten Text — wie bei der Notiz, und aus demselben
    Grund am `window`: Den Fokus hat die Node-Hülle, nicht der Text.
  */
  useEffect(() => {
    if (!selected || isEditing || !editingEnabled) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return
      if (target?.closest?.('[contenteditable]:not([contenteditable="false"])')) return
      // Nur der allein ausgewählte: Bei mehreren wüsste Enter nicht, welchen.
      let count = 0
      for (const node of store.getState().nodeLookup.values()) if (node.selected) count += 1
      if (count !== 1) return
      e.preventDefault()
      setIsEditing(true)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selected, isEditing, editingEnabled, store])

  const fontSize = canvasTextSizeOf(note)

  /*
    ── Die Formatierung (ADR 0029) ──────────────────────────────────────────

    Im Bearbeiten-Modus zeigt die Leiste, was an der Markierung gilt, und
    wirkt auf sie; sonst gilt beides dem ganzen Text.
  */
  const [editor, setEditor] = useState<Editor | null>(null)
  const selectionInfo = useEditorState({
    editor,
    selector: ({ editor: current }) => (current ? canvasTextSelectionOf(current.state) : null),
  })
  const liveFormat = useMemo(() => {
    const base = { color: note.color, font: note.font, fontSize }
    return isEditing && selectionInfo ? canvasTextFormatFrom(selectionInfo, base) : canvasTextFormatOf(note.content, base)
  }, [isEditing, selectionInfo, note.color, note.font, note.content, fontSize])

  /*
    ── Die Vorschau des Farbwählers (CONTEXT.md, Farbwähler) ────────────────

    Drei Wege, je nachdem, wo die Wahl wirken wird:
    - **An der Markierung** (Bearbeiten-Modus): im Schreibfeld, ohne
      Verlaufseintrag (`previewCanvasTextFormat`). Die Leiste zeigt derweil
      den Stand VOR der Vorschau (`frozenFormat`) — sonst wechselte der
      Wähler unter dem Zeiger Ton und Ring.
    - **Am ganzen Text:** nur gerechnet, nicht gespeichert — Tinte über
      `--text-ink`, der Inhalt ohne Abweichungen bzw. mit dem Marker.
    - **Bearbeiten ohne Markierung:** Tinte und Schrift zeigen am ganzen
      Text; der Marker gilt dem, was man als Nächstes tippt, und zeigt nichts.
    Die Schrift geht dieselben Wege wie die Tinte (`--text-font`).
  */
  const [ownColor, setOwnColor] = useOptionPreview(`${note.color}\u0000${note.content}`)
  const [ownFont, setOwnFont] = useOptionPreview<{ font: string | null }>(`${note.font}\u0000${note.content}`)
  const [ownHighlight, setOwnHighlight] = useOptionPreview(note.content)
  const editorPreviewRef = useRef<{ undo: () => void; draft: string } | null>(null)
  const [frozenFormat, setFrozenFormat] = useState<typeof liveFormat | null>(null)
  const format = frozenFormat ?? liveFormat
  const textFormat = useMemo(() => ({
    bold: format.bold,
    italic: format.italic,
    underline: format.underline,
    strike: format.strike,
    highlight: format.highlight,
    list: format.list,
    align: note.textAlign ?? 'left',
    hasMarkers: (isEditing ? draft : note.content).includes('<mark'),
  }), [format, note.textAlign, isEditing, draft, note.content])

  const rangeSelected = () => Boolean(isEditing && editor && !editor.state.selection.empty)

  const draftRef = useRef(draft)
  draftRef.current = draft

  /*
    **Eine Form je Hervorheben:** Vorschau und Klick teilen dieselbe Form für
    neuen Marker, vorhandener behält seine. Gewürfelt wird beim ersten Bedarf,
    neu erst nach dem Klick oder „Marker wechseln" — sonst wechselte die Form bei
    jedem überfahrenen Tropfen (gemeldet 2026-09-24).
  */
  const markerLookRef = useRef<number | null>(null)
  const markerLook = () => (markerLookRef.current ??= randomCanvasTextMarkerLook())
  const endEditorPreview = () => {
    const current = editorPreviewRef.current
    if (!current) return
    editorPreviewRef.current = null
    current.undo()
    setFrozenFormat(null)
  }

  const previewHandler = (kind: 'color' | 'highlight' | 'font') => (preview: CanvasOptionPreview<{ color?: string | null; font?: string | null }>) => {
    if (editor && (editorPreviewRef.current || rangeSelected())) {
      endEditorPreview()
      if (preview === null || preview === 'picked') return
      const draftBefore = draftRef.current
      setFrozenFormat(liveFormat)
      editorPreviewRef.current = {
        draft: draftBefore,
        undo: previewCanvasTextFormat(editor, kind === 'highlight'
          ? { kind: 'highlight', color: (preview.color ?? null) as CanvasTextHighlightKey | null, look: markerLook() }
          : { kind, value: ownStyle(kind, (kind === 'font' ? preview.font : preview.color) ?? null) }),
      }
      return
    }
    // Die Leiste reicht je Knopf genau seine Art durch (`CanvasColorPreview` / `CanvasFontPreview`).
    if (kind === 'font') setOwnFont(preview as CanvasFontPreview)
    else if (kind === 'color') setOwnColor(preview as CanvasColorPreview)
    else if (!isEditing) {
      if (preview !== null && preview !== 'picked') markerLook()
      setOwnHighlight(preview as CanvasColorPreview)
    }
  }

  /*
    Farbe, Schrift, Größe: auf die Markierung als Abweichung — sonst auf den
    ganzen Text als neuer Grundwert, und die Abweichungen gehen mit (im
    selben Schritt; im Bearbeiten-Modus räumt sie das Schreibfeld).
  */
  // Wie der Grundwert: keine Abweichung. Der Standard gegen einen farbigen Grundwert: `default`.
  const ownStyle = (attr: CanvasTextStyleAttr, value: string | number | null) => {
    const baseValue = attr === 'size' ? fontSize : note[BASE_FIELD[attr]]
    return value === baseValue ? null : value ?? CANVAS_TEXT_DEFAULT_KEY
  }

  const setStyle = (attr: CanvasTextStyleAttr, value: string | number | null) => {
    const field = BASE_FIELD[attr]
    if (editor && rangeSelected()) {
      editor.commands.setCanvasTextStyle(attr, ownStyle(attr, value))
      return
    }
    const before: UpdateCanvasNote = { [field]: note[field] }
    const after: UpdateCanvasNote = { [field]: value }
    if (editor && isEditing) {
      editor.commands.clearCanvasTextStyle(attr)
    } else {
      const content = clearCanvasTextStyle(note.content, attr)
      if (content !== note.content) {
        before.content = note.content
        after.content = content
      }
    }
    edit(note.id, before, after)
  }

  const handleTextFormat = (picked: CanvasTextFormatChange) => {
    const change: CanvasTextFormatChange = picked.kind === 'highlight' ? { ...picked, look: markerLook() } : picked
    if (change.kind === 'highlight' || change.kind === 'shuffleMarkers') markerLookRef.current = null
    if (change.kind === 'align') {
      if (change.align !== (note.textAlign ?? 'left')) edit(note.id, { textAlign: note.textAlign }, { textAlign: change.align })
      return
    }
    if (editor && isEditing) {
      applyCanvasTextFormat(editor, change)
      return
    }
    const content = formatCanvasText(note.content, change)
    if (content !== note.content) edit(note.id, { content: note.content }, { content })
  }

  /*
    ── Die Ecken ────────────────────────────────────────────────────────────

    Während des Zugs rechnet `CanvasFlow` die Box (seitenverhältnistreu, mit
    Einrasten); der Text wächst mit, indem die Schrift im Verhältnis der
    Höhen skaliert wird (`--text-live-scale`). Beim Loslassen schreibt
    `CanvasFlow` die neue Schriftgröße — bis sie über die Live-Query zurück
    ist, bleibt die gezogene Größe stehen (`settle`), sonst spränge der Text
    einen Frame lang auf die alte Größe.
  */
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [resize, setResize] = useState<{ startHeight: number; startSize: number; settle: boolean } | null>(null)
  useEffect(() => {
    if (!resize?.settle) return
    if (fontSize !== resize.startSize) {
      setResize(null)
      return
    }
    const timer = setTimeout(() => setResize(null), SETTLE_FALLBACK_MS)
    return () => clearTimeout(timer)
  }, [resize, fontSize])
  /*
    Die Höhe, die der ZUG vorgibt (`node.height`, von `CanvasFlow` gesetzt) —
    nicht `nodeHeight`: Das ist React Flows Messung der Hülle, und die hängt am
    Text, der an diesem Faktor hängt. Eine Rundung um 1 px schaukelte sich so
    Bild für Bild auf, und bei stehendem Zeiger wuchs oder schrumpfte der Text
    von selbst (gemeldet 2026-09-23).
  */
  const pulledHeight = useStore(s => (resize ? s.nodeLookup.get(note.id)?.height : undefined))
  const liveScale = resize && pulledHeight && resize.startHeight > 0 ? pulledHeight / resize.startHeight : 1

  const measureRef = useMeasuredTextSize({
    noteId: note.id,
    stored: { x: note.x, width: note.width, height: note.height },
    align: note.textAlign,
    enabled: resize === null,
    getZoom: () => store.getState().transform[2],
  })

  const fresh = startEditing && isEditing && !selected
  const soloSelected = useSoloSelected(Boolean(selected))
  const pointerBusy = useCanvasPointerBusy(Boolean(selected))

  const rotation = note.rotation ?? 0
  const boxWidth = nodeWidth || note.width
  const boxHeight = nodeHeight || note.height
  const rotationLift = (rotatedBounds(boxWidth, boxHeight, rotation).height - boxHeight) / 2
  const placement = useOptionsBarPlacement(note.y - rotationLift, Boolean(selected) || fresh)

  const showBar = fresh || Boolean(selected && soloSelected && !pointerBusy)
  const barPresence = usePresence(showBar)

  const showRotate = Boolean(selected) && soloSelected && editingEnabled && !isEditing
  const rotate = useRotateDrag({
    getCenter: () => {
      const box = rootRef.current?.getBoundingClientRect()
      return box ? { x: box.left + box.width / 2, y: box.top + box.height / 2 } : null
    },
    getRasten: canvasNoteRotations,
    getStart: () => rotation,
    onCommit: angle => edit(note.id, { rotation }, { rotation: angle }),
    onReset: () => { if (rotation !== 0) edit(note.id, { rotation }, { rotation: 0 }) },
    enabled: showRotate,
  })
  const shownRotation = rotate.preview ?? rotation
  const showResize = Boolean(selected) && soloSelected && editingEnabled && !isEditing && !rotate.rotating

  /*
    Der EINE Ausstieg aus dem Bearbeiten-Modus.

    **Leer verlassen heißt: weg** (CONTEXT.md, Text). Ohne Papier wäre ein
    leerer Text unsichtbar. `CanvasFlow` entscheidet, ob er spurlos geht
    (frisch) oder mit ⌘Z zurückkommt (hatte Inhalt) — er kennt den Stapel.
  */
  const committingRef = useRef(false)
  const commit = useCallback(() => {
    if (committingRef.current) return
    committingRef.current = true
    // Endet der Bearbeiten-Modus mitten in einer Vorschau, gilt der Stand davor.
    const preview = editorPreviewRef.current
    editorPreviewRef.current = null
    const html = preview ? preview.draft : draft
    try {
      preview?.undo()
      setFrozenFormat(null)
      setIsEditing(false)
      if (isCanvasTextEmpty(html)) {
        if (onEmptied) onEmptied(note.id, note.content)
        else void dataSource.deleteCanvasNote(note.id)
        return
      }
      store.getState().addSelectedNodes([note.id])
      if (html !== note.content) edit(note.id, { content: note.content }, { content: html })
    } finally {
      committingRef.current = false
    }
  }, [draft, note.content, note.id, onEmptied, dataSource, store, edit])
  const commitRef = useRef(commit)
  commitRef.current = commit

  // **Abgewählt heißt: nicht im Bearbeiten-Modus** — dieselbe harte Regel wie beim Memo.
  const everSelectedRef = useRef(Boolean(selected))
  if (selected) everSelectedRef.current = true
  useEffect(() => {
    if (selected || store.getState().nodeLookup.get(note.id)?.selected) return
    if (startEditing && !everSelectedRef.current) return
    if (isEditingRef.current) commitRef.current()
  }, [selected, isEditing, startEditing, store, note.id])

  const handleEditorChange = useCallback((html: string, empty: boolean) => {
    setDraft(html)
    setDraftEmpty(empty)
  }, [])

  /*
    Ein Fokuswechsel INNERHALB des Texts schreibt nicht fest (wie beim Memo) —
    und auch keiner in seine **Options-Leiste**: Das Zahlenfeld des Größen-Menüs
    nimmt den Fokus an, und ein frisch abgesetzter, noch leerer Text
    verschwände sonst in dem Moment, in dem man seine Größe eintippt.
  */
  const handleEditorBlur = useCallback((event: FocusEvent) => {
    const next = event.relatedTarget
    if (next instanceof globalThis.Node && rootRef.current?.contains(next)) return
    if (next instanceof Element && next.closest('.canvas-options-bar')) return
    commitRef.current()
  }, [])

  // Hinaus über `blur`, dann der Fokus auf den Node — dieselbe Reihenfolge wie beim Memo.
  const handleEditorExit = useCallback(() => {
    const wrapper = rootRef.current?.closest<HTMLElement>('.react-flow__node')
    ;(document.activeElement as HTMLElement | null)?.blur()
    wrapper?.focus({ preventScroll: true })
  }, [])

  const returnFocus = useCallback(() => {
    const target = isEditingRef.current
      ? rootRef.current?.querySelector<HTMLElement>('[contenteditable]')
      : rootRef.current?.closest<HTMLElement>('.react-flow__node')
    target?.focus({ preventScroll: true })
  }, [])

  const handleResizeStart = useCallback((corner: ResizeCorner) => {
    setResize({ startHeight: rootRef.current?.offsetHeight ?? note.height, startSize: fontSize, settle: false })
    onResizeStart?.(note.id, corner)
  }, [fontSize, note.height, note.id, onResizeStart])
  const handleResizeEnd = useCallback(() => {
    setResize(current => (current ? { ...current, settle: true } : null))
  }, [])

  const placeholder = t('projectCanvas.text.placeholder')

  const inkPreview = ownColor ?? colorPreview
  const fontShown = ownFont ?? fontPreview
  const font = getCanvasNoteFont(fontShown ? fontShown.font : note.font)
  const shownContent = useMemo(() => {
    if (isEditing) return draft
    let content = draft
    if (inkPreview) content = clearCanvasTextStyle(content, 'color')
    if (fontShown) content = clearCanvasTextStyle(content, 'font')
    if (ownHighlight) content = formatCanvasText(content, { kind: 'highlight', color: ownHighlight.color as CanvasTextHighlightKey | null, look: markerLookRef.current ?? undefined })
    return content
  }, [isEditing, draft, inkPreview, fontShown, ownHighlight])

  return (
    <>
      <RotateHandles shown={showRotate} handlers={rotate.handlers} />

      {/* Die Trefferflächen der Ecken — gezeichnet wird im Portal darunter (wie bei der Notiz). */}
      {showResize && RESIZE_CORNERS.map(position => (
        <NodeResizeControl
          key={position}
          position={position}
          keepAspectRatio
          minWidth={4}
          minHeight={MIN_CANVAS_TEXT_SIZE}
          /*
            Die Klasse der Notiz, nicht eine eigene: Größe und Lage der
            Trefferfläche sind dieselben, und `.canvas-flow--multi-select` nimmt
            sie beim Mehrfachauswählen schon mit weg.
          */
          className="note-node__handle"
          onResizeStart={() => handleResizeStart(position)}
          onResizeEnd={handleResizeEnd}
        />
      ))}

      {showResize && (
        <ViewportPortal>
          {RESIZE_CORNERS.map(position => (
            <div
              key={position}
              className="canvas-note-handle"
              style={{
                left: position.endsWith('left') ? positionAbsoluteX : positionAbsoluteX + boxWidth,
                top: position.startsWith('top') ? positionAbsoluteY : positionAbsoluteY + boxHeight,
                scale: 'var(--canvas-scale, 1)',
              }}
            />
          ))}
        </ViewportPortal>
      )}

      <NodeToolbar
        isVisible={barPresence.mounted}
        position={placement === 'below' ? Position.Bottom : Position.Top}
        offset={OPTIONS_BAR_OFFSET + rotationLift}
      >
        <CanvasOptionsBar
          leaving={barPresence.leaving}
          onActionDone={returnFocus}
          buttons={BAR_BUTTONS}
          colorKind="ink"
          colorKeys={format.colors}
          fontKeys={format.fonts}
          fontSizes={format.sizes}
          textFormat={textFormat}
          onTextFormat={handleTextFormat}
          placement={placement}
          onColorChange={key => setStyle('color', key)}
          // Die **Standard**-Tinte ist `null` — ein eigener Rückruf, weil die
          // Palettenwahl nur Schlüssel kennt.
          onColorReset={() => setStyle('color', null)}
          onColorPreview={previewHandler('color')}
          onHighlightPreview={previewHandler('highlight')}
          onFontPreview={previewHandler('font')}
          onFontChange={key => setStyle('font', key)}
          onFontSizeChange={size => setStyle('size', size)}
          onDelete={() => (onDelete ? onDelete(note.id) : void dataSource.deleteCanvasNote(note.id))}
        />
      </NodeToolbar>

      <div
        ref={element => { measureRef(element); rootRef.current = element }}
        className={'text-node' + (isEditing ? ' text-node--editing' : '')}
        style={{
          '--text-size': `${fontSize}px`,
          '--text-font-scale': font.scale,
          '--text-live-scale': liveScale,
          '--text-font': font.stack,
          '--marker-font-shift': `${font.markerShift}em`,
          '--text-font-weight': font.weight,
          '--text-ink': getCanvasTextInk(inkPreview ? inkPreview.color : note.color),
          '--text-align': note.textAlign ?? 'left',
          // Im Bearbeiten-Modus richtet sich der Text auf — wie das Memo.
          '--note-angle': `${isEditing ? 0 : shownRotation}deg`,
        } as React.CSSProperties}
        onDoubleClick={openEditor}
      >
        {isEditing ? (
          <>
            {/*
              Der Platzhalter steht IM FLUSS und gibt der leeren Box ihre Breite
              (CONTEXT.md, Text); das Feld liegt darüber, der Caret links auf
              ihm. Ein CSS-`::before` wie TipTaps eigener Platzhalter trüge zur
              Breite nichts bei.
            */}
            {draftEmpty && <span className="text-node__placeholder" aria-hidden="true">{placeholder}</span>}
            <div className={'text-node__field' + (draftEmpty ? ' text-node__field--over' : '')}>
              <CanvasTextEditor
                content={draft}
                label={t('projectCanvas.text.editorLabel')}
                onChange={handleEditorChange}
                onExit={handleEditorExit}
                onBlur={handleEditorBlur}
                onReady={setEditor}
                seed={note.id}
              />
            </div>
          </>
        ) : draftEmpty ? (
          // Ein leerer Text in Ruhe kommt nur von außen (Sync, ältere Fassung) —
          // sichtbar gedämpft, damit man ihn findet.
          <span className="text-node__placeholder">{placeholder}</span>
        ) : (
          <CanvasTextContent content={shownContent} seed={note.id} />
        )}
      </div>
    </>
  )
}
